import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Grid,
  Skeleton,
  Typography,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import { useSnackbar } from 'notistack';
import { DialogHeader } from './DialogHeader';
import { sageSupplierLinkingService } from '../../services/sageSupplierLinkingService';
import { SageSupplierDetails } from '../../types/sage';

interface SageLinkSupplierDialogProps {
  open: boolean;
  supplierName: string;
  sageSupplierId: number;
  entityType: string;
  entityId: number;
  onClose: () => void;
  onLinked: () => void;
}

/**
 * Dialog for linking an existing Sage supplier to a DRIS entity.
 * Shown when Sage rejects supplier creation because the name already exists.
 * Fetches supplier details from Sage and offers to link + sync data.
 */
export const SageLinkSupplierDialog: React.FC<SageLinkSupplierDialogProps> = ({
  open,
  supplierName,
  sageSupplierId,
  entityType,
  entityId,
  onClose,
  onLinked,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [supplier, setSupplier] = useState<SageSupplierDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [linking, setLinking] = useState(false);
  const [syncDataToDris, setSyncDataToDris] = useState(true);

  // Fetch supplier details when dialog opens
  useEffect(() => {
    if (!open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- resets dialog state when it closes and sets loading before the async Sage fetch; keyed on `open`, no render-time equivalent
      setSupplier(null);
      setLoading(false);
      setLinking(false);
      setSyncDataToDris(true);
      return;
    }

    let cancelled = false;
    setLoading(true);
    sageSupplierLinkingService
      .findExistingSupplier(entityType, entityId)
      .then((result) => {
        if (!cancelled) setSupplier(result);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [open, entityType, entityId]);

  const handleLink = useCallback(async () => {
    setLinking(true);
    try {
      await sageSupplierLinkingService.linkExistingSupplier(entityType, entityId, {
        sageSupplierId,
        syncDataToDris,
      });
      enqueueSnackbar('Supplier linked successfully', { variant: 'success' });
      onLinked();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to link supplier';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setLinking(false);
    }
  }, [entityType, entityId, sageSupplierId, syncDataToDris, enqueueSnackbar, onLinked]);

  const formatAddress = (lines: (string | undefined | null)[]): string => {
    return lines.filter(Boolean).join(', ');
  };

  return (
    <Dialog
      open={open}
      onClose={linking ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogHeader
        title="Link Existing Sage Supplier"
        icon={<LinkIcon color="primary" />}
        onClose={onClose}
        disabled={linking}
      />

      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          A supplier named <strong>{supplierName}</strong> already exists in Sage.
          Link it to this {entityType.toLowerCase()} to continue.
        </Alert>

        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Skeleton variant="text" width="60%" />
            <Skeleton variant="text" width="80%" />
            <Skeleton variant="text" width="40%" />
            <Skeleton variant="text" width="70%" />
          </Box>
        ) : supplier ? (
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Sage Supplier Details
            </Typography>
            <Grid container spacing={1} sx={{ mb: 2 }}>
              <DetailRow label="Name" value={supplier.name} />
              <DetailRow label="Contact" value={supplier.contactName} />
              <DetailRow label="Email" value={supplier.email} />
              <DetailRow label="Phone" value={supplier.telephone} />
              <DetailRow label="Mobile" value={supplier.mobile} />
              <DetailRow label="Tax Ref" value={supplier.taxReference} />
              <DetailRow
                label="Postal Address"
                value={formatAddress([
                  supplier.postalAddress01,
                  supplier.postalAddress02,
                  supplier.postalAddress03,
                  supplier.postalAddress04,
                  supplier.postalAddress05,
                ])}
              />
              <DetailRow
                label="Delivery Address"
                value={formatAddress([
                  supplier.deliveryAddress01,
                  supplier.deliveryAddress02,
                  supplier.deliveryAddress03,
                  supplier.deliveryAddress04,
                  supplier.deliveryAddress05,
                ])}
              />
            </Grid>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Could not load supplier details from Sage. You can still proceed with linking.
          </Typography>
        )}

        <FormControlLabel
          control={
            <Checkbox
              checked={syncDataToDris}
              onChange={(e) => setSyncDataToDris(e.target.checked)}
              disabled={linking}
            />
          }
          label="Sync contact details from Sage to DRIS"
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={linking}>
          Cancel
        </Button>
        <Button
          onClick={handleLink}
          variant="contained"
          disabled={linking}
          startIcon={
            linking ? <CircularProgress size={16} color="inherit" /> : <LinkIcon />
          }
        >
          Link & Sync
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** Read-only detail row for displaying supplier info. */
const DetailRow: React.FC<{ label: string; value?: string | null }> = ({ label, value }) => {
  if (!value) return null;
  return (
    <>
      <Grid size={{ xs: 4 }}>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </Grid>
      <Grid size={{ xs: 8 }}>
        <Typography variant="body2">{value}</Typography>
      </Grid>
    </>
  );
};

export default SageLinkSupplierDialog;
